/**
 * Distance Calculations
 *
 * Functions for calculating distances between positions in 3D space
 */

import {Position} from "../../types/position";

/**
 * Calculate 3D Euclidean distance between two positions
 * distance = √(Δx² + Δy² + Δz²)
 *
 * @param a First position
 * @param b Second position
 * @returns Distance in the same units as input positions
 */
export function getDistanceBetween(a: Position, b: Position): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Calculate horizontal distance (XZ plane only, ignoring Y/height)
 * Useful for spatial audio where vertical distance matters less
 *
 * @param a First position
 * @param b Second position
 * @returns Horizontal distance
 */
export function getHorizontalDistance(a: Position, b: Position): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  return Math.sqrt(dx * dx + dz * dz);
}

/**
 * Check if a position is within a certain range of another
 *
 * @param a First position
 * @param b Second position
 * @param maxDistance Maximum allowed distance
 * @returns True if within range
 */
export function isWithinRange(
  a: Position,
  b: Position,
  maxDistance: number
): boolean {
  return getDistanceBetween(a, b) <= maxDistance;
}
